import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MinusIcon, PlusIcon, ShoppingBagIcon, XIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { TextField } from '../components/forms/TextField';
import { FormSuccess } from '../components/forms/FormSuccess';
import { useCart } from '../contexts/CartContext';
import { formatMoney } from '../utils/price';
export function Cart() {
  const {
    items,
    subtotal,
    updateQty,
    removeItem,
    clear
  } = useCart();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done'>('idle');
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    window.setTimeout(() => {
      clear();
      setStatus('done');
    }, 900);
  };
  return <section className="mx-auto max-w-6xl px-6 pb-24 pt-32 xl:pt-44" data-id="element-378" data-mp="5u4weag8xu7k">
      <h1 className="font-slab text-4xl font-bold uppercase tracking-[0.12em]" data-id="element-379" data-mp="5vg1cdlfh809">Cart</h1>

      {status === 'done' ? <div className="mt-10" data-id="element-380" data-mp="29jkp0atwhevz">
          <FormSuccess title="Order received" data-id="element-381" data-mp="29ij4lfqzvg1l">
            Thanks! We’ve got your order and will reach out to confirm payment and pickup or shipping details.
          </FormSuccess>
        </div> : items.length === 0 ? <div className="mt-10 flex flex-col items-center border border-ink-line px-6 py-20 text-center" data-id="element-382" data-mp="1zj2031nhb877">
          <ShoppingBagIcon className="h-12 w-12" strokeWidth={1} aria-hidden="true" data-id="element-383" data-mp="24i536gjwf4cj" />
          <p className="mt-5 text-lg text-ink-muted" data-id="element-384" data-mp="2salmsvjab4dy">Your cart is currently empty.</p>
          <Button to="/shop" className="mt-8" data-id="element-385" data-mp="2x8dg9tzkuyk9">
            Return to shop
          </Button>
        </div> : <div className="mt-10 grid gap-12 lg:grid-cols-[minmax(0,1fr)_380px]" data-id="element-386" data-mp="2jb6zu62btby7">
          <ul className="divide-y divide-ink-line border-y border-ink-line" data-id="element-387" data-mp="2n94o96o9vu0j">
            {items.map(item => <li key={item.key} className="flex gap-5 py-6" data-id="element-388" data-mp="17snurttj7475">
                <div className="h-24 w-24 shrink-0 overflow-hidden bg-ink-surface" data-id="element-389" data-mp="1bwlqrvnk7yvz">
                  {item.image && <img src={item.image} alt="" className="h-full w-full object-cover" data-id="element-390" data-mp="3eh67qqnz2n34" />}
                </div>
                <div className="flex min-w-0 flex-1 flex-col" data-id="element-391" data-mp="39kfsg5jzrvir">
                  <div className="flex items-start justify-between gap-4" data-id="element-392" data-mp="34s4rr0hfg5p5">
                    <div data-id="element-393" data-mp="34q1v3xlrrebg">
                      <Link to={`/product/${item.slug}`} className="font-slab font-bold uppercase tracking-[0.08em] hover:underline" data-id="element-394" data-mp="2ztv02zuwvn8a">
                        {item.name}
                      </Link>
                      {item.option && <p className="mt-1 text-sm text-ink-muted" data-id="element-395" data-mp="2qrlos7rtjptu">{item.option}</p>}
                    </div>
                    <button type="button" onClick={() => removeItem(item.key)} className="p-1 text-ink-muted hover:text-white" aria-label={`Remove ${item.name}`} data-id="element-396" data-mp="2lull23ifyz5w">
                      <XIcon className="h-4 w-4" data-id="element-397" data-mp="k1xa17zk37fk" />
                    </button>
                  </div>
                  <div className="mt-auto flex items-center justify-between pt-4" data-id="element-398" data-mp="f3cc5f7s0zua">
                    <div className="flex items-center border border-ink-line" data-id="element-399" data-mp="nk36onlb9q7m">
                      <button type="button" onClick={() => updateQty(item.key, item.qty - 1)} className="px-2.5 py-2 hover:opacity-60" aria-label="Decrease quantity" data-id="element-400" data-mp="ni0jebbjmb6l">
                        <MinusIcon className="h-3.5 w-3.5" data-id="element-401" data-mp="wdpzc4u82dcl" />
                      </button>
                      <span className="w-7 text-center font-slab text-sm" data-id="element-402" data-mp="11agf0r2476xh">{item.qty}</span>
                      <button type="button" onClick={() => updateQty(item.key, item.qty + 1)} className="px-2.5 py-2 hover:opacity-60" aria-label="Increase quantity" data-id="element-403" data-mp="4m0pguz08xkd">
                        <PlusIcon className="h-3.5 w-3.5" data-id="element-404" data-mp="dlx74cmz8w5x" />
                      </button>
                    </div>
                    <p className="font-slab tracking-wider" data-id="element-405" data-mp="ilji0ffc20bk">{formatMoney(item.price * item.qty)}</p>
                  </div>
                </div>
              </li>)}
          </ul>

          <form onSubmit={handleSubmit} className="h-fit space-y-5 border border-ink-line bg-ink-surface p-6" data-id="element-406" data-mp="1tzx1z96ytvmb">
            <h2 className="font-slab text-sm font-bold uppercase tracking-[0.2em]" data-id="element-407" data-mp="32wltd2aiwo5r">Order summary</h2>
            <div className="flex items-center justify-between border-b border-ink-line pb-4" data-id="element-408" data-mp="2xykdeq8e94yi">
              <span className="text-ink-muted" data-id="element-409" data-mp="3bybks84enn3j">Subtotal</span>
              <span className="font-slab text-xl tracking-wider" data-id="element-410" data-mp="3bvpcu7dlr5ks">{formatMoney(subtotal)}</span>
            </div>
            <TextField label="Full name" name="cart-name" required autoComplete="name" data-id="element-411" data-mp="3lvzr7vbdij1j" />
            <TextField label="Email" name="cart-email" type="email" required autoComplete="email" data-id="element-412" data-mp="3ltmziqftcuba" />
            <TextField label="Phone" name="cart-phone" type="tel" autoComplete="tel" data-id="element-413" data-mp="3gv2a7m23nv1m" />
            <Button type="submit" loading={status === 'submitting'} className="w-full" data-id="element-414" data-mp="3uni9cbrvr8x9">
              Place order
            </Button>
            <p className="text-xs leading-relaxed text-ink-faint" data-id="element-415" data-mp="8gtngum9dyhd">We’ll contact you to confirm payment. Gift certificates and deposits are non-refundable.</p>
          </form>
        </div>}
    </section>;
}